import { Company, Team, User } from '@run-diffusion/shared';
import { startedInBoardsV2 } from '@/utils/boardsTour';

/**
 * Boards v1 (the classic list, `/runnit/boards`) and Boards v2 (the canvas
 * boards' list, `/runnit/boards-v2`) are separate pages, each listing only its
 * own boards, with a switch between them. Which one the side nav's Boards
 * opens is the one chosen last, in this browser: Boards v2's list opened (or
 * switched to), or the switch to Classic. Landing on v1's page any other way
 * (a link, an account without Boards v2 sent there) changes nothing.
 */
export type BoardsVersion = 'v1' | 'v2';

const KEY = 'rd-boards-version';

/**
 * Whether the account being acted as has Boards v2: its list, its boards, and
 * the switch on v1's page. RunDiffusion rolls it out a team at a time, then a
 * whole company (`features.boardsV2` on the team or on its company, both set on
 * the Boards v2 Rollout admin page), and its own admins always have it. So do
 * the people in its alpha (`features.boardsV2` on the user, added from a CSV of
 * emails on the same page, IL-107), wherever they work: their personal account
 * and every team. A personal account also has it once it began in Boards v2: a
 * new account sent through the Boards v2 tour after its survey (IL-095,
 * utils/boardsTour.ts), while new accounts are switched on. Other personal
 * accounts aren't in the rollout yet.
 *
 * `loading` until the user, and in team mode the team and its company, have
 * loaded: decide nothing (and send no one away) on it.
 */
export type BoardsV2Access = 'on' | 'off' | 'loading';

export type BoardsV2Team = Pick<Team, 'features'> & {
	// vuefire leaves a reference it hasn't loaded yet as its path
	company?: Pick<Company, 'features'> | string | null
};

export function boardsV2Access (
	user: Pick<User, 'isAdmin' | 'teamIds' | 'featureToursCompletedMap' | 'features'> | null | undefined,
	team: BoardsV2Team | null | undefined,
	isTeamMode: boolean,
): BoardsV2Access {
	if (!user) return 'loading';
	if (user.isAdmin || user.features?.boardsV2) return 'on';
	if (!isTeamMode) return startedInBoardsV2(user) ? 'on' : 'off';
	if (!team) return Object.keys(user.teamIds || {}).length ? 'loading' : 'off';
	if (typeof team.company === 'string') return 'loading';
	return team.features?.boardsV2 || team.company?.features?.boardsV2 ? 'on' : 'off';
}

export function rememberBoardsVersion (version: BoardsVersion): void {
	try {
		window.localStorage.setItem(KEY, version);
	} catch (e) {
		// private mode or full: the side nav opens the classic list
	}
}

export function lastBoardsVersion (): BoardsVersion {
	try {
		return window.localStorage.getItem(KEY) === 'v2' ? 'v2' : 'v1';
	} catch (e) {
		return 'v1';
	}
}
